import type { EntityData } from '@adornis/baseql/entities/types.js';
import { confirmDialog } from '@adornis/dialog/dialog-functions.js';
import { FileInput } from '@adornis/file-utils/client/file-input.js';
import { DateInput } from '@adornis/formfield/fields/date-input.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { ToggleInput } from '@adornis/formfield/fields/toggle-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful, useObservable, useState } from '@adornis/functional-lit/stateful.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ValidationError } from '@adornis/validation/errors/ValidationError.js';
import { and } from '@adornis/validation/functions/and.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { differenceInYears, isBefore } from 'date-fns';
import { html, nothing } from 'lit';
import { repeat } from 'lit/directives/repeat.js';
import '../../../../client/components/sg-file-input.js';
import { Country, Familienstand } from '../../../../db/enums/misc-enums.js';
import { useMartinusABAC } from '../../../../permissions/client/useMartinusAbac.js';
import type { Person } from '../../../entities/person.js';
import type { UserOnUserPayload } from '../../../UserPolicy.js';
import { MitarbeiterKind } from '../../entities/employee-sections/an-infos.js';
import { UserEmployeeDetailsActions } from '../../UserEmployeeDetailsPolicy.js';

const ChildContent = stateful((props: { form: FunctionalForm<EntityData<Person>>; update: () => void }) => {
  if (!props.form.document?.mitarbeiterDaten?.anInfos?.hatKinder) return nothing;

  const { can: canEditSections } = useMartinusABAC<UserOnUserPayload>({
    action: UserEmployeeDetailsActions.EDIT_EMPLOYEE_SECTION_DETAILS,
    viewedUser: props.form.document,
  });

  const user = useObservable(() => currentUser<Person>(), []);
  if (!user) return nothing;

  return html`
    <div class="col-span-full flex flex-col gap-6">
      <div class="flex flex-col gap-3 pl-4 border-l-2 border-base-300">
        <p class="text-sm text-base-content/70">
          Für den Nachweis von Kindern bezahlen Sie weniger Pflegeversicherung, erfassen Sie dafür bitte alle Ihre
          Kinder
        </p>
        ${
          props.form.document.mitarbeiterDaten.anInfos.kinder?.length
            ? html`
                ${repeat(props.form.document.mitarbeiterDaten.anInfos.kinder, (child, idx) =>
                  ChildLine({ form: props.form, child, idx, update: props.update }),
                )}
              `
            : nothing
        }
        ${
          canEditSections
            ? html`
                <button
                  type="button"
                  class="btn btn-dash btn-block justify-start"
                  @click=${() => {
                    if (!props.form.document?.mitarbeiterDaten?.anInfos) return;
                    props.form.document.mitarbeiterDaten.anInfos.kinder?.push(new MitarbeiterKind({}));
                    props.update();
                  }}
                >
                  <span class="icon-[material-symbols--add]"></span>Kind hinzufügen
                </button>
              `
            : nothing
        }
      </div>
      <div class="flex flex-col gap-1">
        ${FileInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'elternschaftsnachweisID'),
          placeholder: 'Elternschaftsnachweis',
          placeholderMode: 'prefix',
        })}

        <p class="text-sm text-base-content/70">
          Geburts- oder Adoptionsurkunde, Kindergeldbescheid, etc. für eines der Kinder (ein Dokument ist ausreichend)
        </p>
      </div>
    </div>
  `;
});

const ChildLine = stateful(
  (props: { form: FunctionalForm<EntityData<Person>>; child: MitarbeiterKind; idx: number; update: () => void }) => {
    const age = props.child.geburtsdatum ? differenceInYears(new Date(), props.child.geburtsdatum as Date) : null;
    return html`
      <div class="flex flex-row items-center gap-2">
        ${TextInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'kinder', props.idx, 'vorname'),
          placeholder: 'Vorname',
        })}
        ${TextInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'kinder', props.idx, 'nachname'),
          placeholder: 'Nachname',
        })}
        ${DateInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'kinder', props.idx, 'geburtsdatum'),
          placeholder: 'Geburtsdatum',
        })}
        <span class="text-sm text-base-content/70 whitespace-nowrap">
          ${age ? Math.floor(Math.abs(age)) : '--'} Jahre alt
        </span>
        <button
          class="btn btn-ghost"
          @click=${() => {
            props.form.document?.mitarbeiterDaten?.anInfos?.kinder?.splice(props.idx, 1);
            props.update();
          }}
        >
          <span class="icon-[material-symbols--person-remove] text-lg"></span>
        </button>
      </div>
    `;
  },
);

export const EmployeeSectionInfosForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  const [manuelFormUpdate, setManuelFormUpdate] = useState(0);
  const manuallyUpdateForm = () => setManuelFormUpdate(manuelFormUpdate + 1);

  const workReqLicense =
    props.form.document?.mitarbeiterDaten?.anInfos &&
    ![
      props.form.document.profilDaten?.stammdaten?.staatsangehoerigkeit,
      props.form.document.profilDaten?.stammdaten?.staatsangehoerigkeit2,
    ].includes(Country.GERMANY);

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Geburtsname',
        render: TextInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'geburtsName'),
          placeholder: 'Kein Geburtsname hinterlegt',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Familienstand',
        render: DropdownSelectionInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'familienstand'),
          placeholder: 'Kein Familienstand hinterlegt',
          placeholderMode: 'hide',
          selectables: Object.values(Familienstand),
          clearable: true,
        }),
      })}

      <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">Informationen zu Kindern</h4>

      ${FormGridEntry({
        label: 'Haben Sie Kinder?',
        render: ToggleInput({
          ...props.form.field('mitarbeiterDaten', 'anInfos', 'hatKinder'),
          label: props.form.document?.mitarbeiterDaten?.anInfos?.hatKinder
            ? 'Ja, ich habe Kinder'
            : 'Nein, ich habe keine Kinder',
          onPicked: async (value: boolean | null) => {
            if (!props.form.document?.mitarbeiterDaten?.anInfos) return;

            /**
             * Person has children but no children in form -> we create the first one
             */
            if (value && !props.form.document.mitarbeiterDaten.anInfos.kinder?.length) {
              props.form.document.mitarbeiterDaten.anInfos.kinder = [new MitarbeiterKind({})];
            }
            /**
             *
             * Person has no children but children in form -> we remove them
             */
            if (!value && props.form.document.mitarbeiterDaten.anInfos.kinder?.length) {
              const deleteChildren = await confirmDialog('Möchten Sie die Daten der eingetragenen Kinder löschen?', {
                highlightDecline: true,
              });
              if (deleteChildren) {
                props.form.document.mitarbeiterDaten.anInfos.kinder = [];
              }
            }

            props.form.document.mitarbeiterDaten.anInfos.hatKinder = value ?? false;

            manuallyUpdateForm();
          },
        }),
      })}
      ${
        props.form.document?.mitarbeiterDaten?.anInfos?.hatKinder ||
        // Only for children below an age of 23 years
        (props.form.document?.profilDaten?.stammdaten?.geburtsDatum
          ? differenceInYears(new Date(), props.form.document.profilDaten.stammdaten.geburtsDatum) < 23
          : false)
          ? nothing
          : html`
              <p class="col-span-full text-sm text-base-content/70">
                Sie zahlen einen höheren Beitrag zur Pflegeversicherung (Zuschlag für Kinderlose)
              </p>
            `
      }
      ${ChildContent({ form: props.form, update: manuallyUpdateForm })}
      ${
        workReqLicense
          ? html`
              <h4 class="col-span-full mt-6 border-b border-base-300 pb-1 font-semibold">
                Aufenthalts- und Arbeitserlaubnis
              </h4>
              ${FormGridEntry({
                label: 'Benötigen Sie eine Aufenthaltserlaubnis?',
                render: ToggleInput({
                  ...props.form.field('mitarbeiterDaten', 'anInfos', 'hatAufenthaltserlaubnis'),
                  label: props.form.document?.mitarbeiterDaten?.anInfos?.hatAufenthaltserlaubnis
                    ? 'Ja, benötigt Aufenthaltserlaubnis'
                    : 'Nein, benötigt keine Aufenthaltserlaubnis',
                }),
              })}
              ${
                props.form.document?.mitarbeiterDaten?.anInfos?.hatAufenthaltserlaubnis
                  ? html`
                      ${FormGridEntry({
                        label: 'Kopie Aufenthaltserlaubnis',
                        render: FileInput({
                          ...props.form.field('mitarbeiterDaten', 'anInfos', 'aufenthaltsgenehmigungID'),
                          placeholder: 'Keine Kopie Aufenthaltserlaubniskopie hinterlegt',
                          placeholderMode: 'hide',
                        }),
                      })}
                    `
                  : nothing
              }
              ${FormGridEntry({
                label: 'Benötigen Sie eine Arbeitserlaubnis?',
                render: ToggleInput({
                  ...props.form.field('mitarbeiterDaten', 'anInfos', 'hatArbeitserlaubnis'),
                  label: props.form.document?.mitarbeiterDaten?.anInfos?.hatArbeitserlaubnis
                    ? 'Ja, benötigt Arbeitserlaubnis'
                    : 'Nein, benötigt keine Arbeitserlaubnis',
                }),
              })}
              ${
                props.form.document?.mitarbeiterDaten?.anInfos?.hatArbeitserlaubnis
                  ? html`
                      ${FormGridEntry({
                        label: 'Kopie Arbeitserlaubnis',
                        render: FileInput({
                          ...props.form.field('mitarbeiterDaten', 'anInfos', 'arbeitserlaubnisID'),
                          placeholder: 'Keine Kopie Arbeitserlaubnis hinterlegt',
                          placeholderMode: 'hide',
                        }),
                      })}
                      ${FormGridEntry({
                        label: 'Gültig ab',
                        render: DateInput({
                          ...props.form.field('mitarbeiterDaten', 'anInfos', 'gueltigAb'),
                          placeholder: 'Kein Datum hinterlegt',
                          placeholderMode: 'hide',
                          clearable: true,
                        }),
                      })}
                      ${FormGridEntry({
                        label: 'Ausstellungsdatum',
                        render: DateInput({
                          ...props.form.field('mitarbeiterDaten', 'anInfos', 'ausstellungsDatumArbeitserlaubnis'),
                          placeholder: 'Kein Datum hinterlegt',
                          placeholderMode: 'hide',
                          clearable: true,
                        }),
                      })}
                      ${FormGridEntry({
                        label: 'Gültig bis',
                        render: DateInput({
                          ...props.form.field('mitarbeiterDaten', 'anInfos', 'gueltigBis'),
                          placeholder: 'Kein Datum hinterlegt',
                          placeholderMode: 'hide',
                          clearable: true,
                        }),
                      })}
                    `
                  : nothing
              }
            `
          : nothing
      }
    </div>
  `;
});

export const EmployeeSectionInfosFormValidator = {
  familienstand: options => {
    return sgNonOptional()(options);
  },
  hatAufenthaltserlaubnis: sgNonOptional({ allowFalsy: true }),
  aufenthaltsgenehmigungID: options => {
    if (!options.source?.mitarbeiterDaten?.anInfos?.hatAufenthaltserlaubnis) return;
    return sgNonOptional()(options);
  },
  hatArbeitserlaubnis: sgNonOptional({ allowFalsy: true }),
  arbeitserlaubnisID: options => {
    if (!options.source?.mitarbeiterDaten?.anInfos?.hatArbeitserlaubnis) return;
    return sgNonOptional()(options);
  },
  kinder: {
    vorname: sgNonOptional(),
    nachname: sgNonOptional(),
    geburtsdatum: and(sgNonOptional(), v => {
      if (isBefore(v.target.geburtsDatum, new Date(Date.UTC(1945, 0, 1))))
        throw new ValidationError(`Geburtsdatum darf nicht kleiner 1945 sein!`, {
          key: 'geburtsDatum',
          translationKey: 'validation_date_to_old',
          path: v.path,
        });
    }),
  },
};
